import { useCallback, useEffect, useRef } from 'react';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getCalls, getCallsSummary, getDecisions, type CallItem, type CallsPage, type CallsScopeKind,
} from '@/api/calls';
import { useProject } from '@/contexts/ProjectProvider';
import { badgeRequest, callsRequest, missedOpenCount, serverAppliedMine, type CallsView } from '@/lib/callsQuery';
import { CALL_EVENT_TYPES, RESYNC } from '@/lib/crmEvents';
import { useCrmEvents } from './useCrmEvents';

/** Ключи кэша «Звонков». Проект — в ключе: у каждого проекта своя область CRM. */
export const callsKeys = {
  all: (p: string) => ['crm', 'calls', p] as const,
  list: (p: string, q: Record<string, string>) => ['crm', 'calls', p, 'list', q] as const,
  summary: (p: string, q: Record<string, string>) => ['crm', 'calls', p, 'summary', q] as const,
  badge: (p: string) => ['crm', 'calls', p, 'badge'] as const,
  decisions: (p: string) => ['crm', 'calls', p, 'decisions'] as const,
  card: (p: string, id: string) => ['crm', 'calls', p, 'card', id] as const,
  transcript: (p: string, id: string) => ['crm', 'calls', p, 'transcript', id] as const,
  review: (p: string, id: string) => ['crm', 'calls', p, 'review', id] as const,
};

/** Последняя известная область звонков (по проекту) — чтобы с первого запроса спрашивать правильно. */
const scopeMemo = new Map<string, CallsScopeKind>();
const SCOPE_KEY = 'saleus-calls-scope:';
export function rememberedScope(project: string): CallsScopeKind | null {
  const m = scopeMemo.get(project);
  if (m) return m;
  try {
    const v = localStorage.getItem(SCOPE_KEY + project);
    return v === 'all' || v === 'branch' || v === 'own' ? v : null;
  } catch { return null; }
}
function rememberScope(project: string, s: CallsScopeKind) {
  if (scopeMemo.get(project) === s) return;
  scopeMemo.set(project, s);
  try { localStorage.setItem(SCOPE_KEY + project, s); } catch { /* приватный режим — живём без памяти */ }
}

/**
 * Понимает ли сервер отбор mine=1 (узнаём по первому ответу; null — ещё не спрашивали). Один раз
 * поняв, что сервер старый, больше не тратим на это запрос.
 */
let serverMine: boolean | null = null;

/**
 * Страница журнала. В режиме «мои» у руководителя сначала — вкладка + mine=1; сервер не подтвердил
 * отбор (старый) — та же страница прежним путём (tab=mine), её вид отберёт телефон.
 */
async function journalPage(req: ReturnType<typeof callsRequest>, cursor: string | null): Promise<CallsPage> {
  const withCursor = (p: Record<string, string>) => (cursor ? { ...p, cursor } : p);
  if (!req.legacy || serverMine !== false) {
    const page = await getCalls(withCursor(req.list));
    if (!req.legacy) return page;
    if (serverAppliedMine(page)) { serverMine = true; return page; }
    // Курсор у двух запросов разный: старый сервер узнаётся на первой странице, дальше идём только прежним путём.
    serverMine = false;
    if (cursor) return { ...(await getCalls(req.legacy.list)), legacyMine: true };
  }
  return { ...(await getCalls(withCursor(req.legacy.list))), legacyMine: true };
}

/**
 * Журнал: страницы по курсору, сводка за период. «Сейчас» фиксируется по дню: смена дня меняет ключ.
 */
export function useCallsJournal(view: CallsView, scope: CallsScopeKind | null) {
  const { status, projectId } = useProject();
  const enabled = status === 'ready' && !!projectId;
  const day = new Date().toDateString();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const req = useCallsRequestMemo(view, scope, day);

  const list = useInfiniteQuery<CallsPage, Error>({
    queryKey: callsKeys.list(projectId, req.list),
    enabled,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => journalPage(req, pageParam ? String(pageParam) : null),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    staleTime: 60_000,
  });

  const firstScope = list.data?.pages[0]?.scope;
  useEffect(() => { if (firstScope && projectId) rememberScope(projectId, firstScope); }, [firstScope, projectId]);

  const summary = useQuery({
    queryKey: callsKeys.summary(projectId, req.summary),
    enabled,
    queryFn: () => getCallsSummary(req.summary),
    staleTime: 60_000,
  });
  return { list, summary, req, project: projectId, enabled };
}

function useCallsRequestMemo(view: CallsView, scope: CallsScopeKind | null, day: string) {
  const ref = useRef<{ k: string; v: ReturnType<typeof callsRequest> } | null>(null);
  const k = `${view.kind}|${view.period}|${view.mine}|${scope}|${day}`;
  if (!ref.current || ref.current.k !== k) ref.current = { k, v: callsRequest(view, scope) };
  return ref.current.v;
}

/** Итоги звонков «Ждут вашего решения» — общий кэш для блока и значка вкладки. */
export function useDecisions() {
  const { status, projectId } = useProject();
  return useQuery({
    queryKey: callsKeys.decisions(projectId),
    enabled: status === 'ready' && !!projectId,
    queryFn: getDecisions,
    staleTime: 60_000,
    refetchInterval: 3 * 60_000,
  });
}

/** Мои звонки за 7 дней — для значка «не перезвонили». */
export function useMissedOpen() {
  const { status, projectId } = useProject();
  return useQuery({
    queryKey: callsKeys.badge(projectId),
    enabled: status === 'ready' && !!projectId,
    queryFn: async () => {
      const r = await getCalls(badgeRequest());
      return { count: missedOpenCount(r.items), items: r.items as CallItem[] };
    },
    staleTime: 60_000,
    refetchInterval: 3 * 60_000,
  });
}

/** Значок вкладки «Звонки» = пропущенные без перезвона (мои, по клиентам) + итоги, ждущие решения. */
export function useCallsBadge(): number {
  const missed = useMissedOpen();
  const dec = useDecisions();
  return (missed.data?.count ?? 0) + (dec.data?.total ?? 0);
}

/**
 * Живые обновления «Звонков» и календаря из ленты CRM. Держится в оболочке (всегда), перечитывает
 * одной пачкой: АТС шлёт по нескольку событий на разговор.
 */
export function useCallsLive() {
  const qc = useQueryClient();
  const { status, projectId } = useProject();
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const want = useRef({ calls: false, decisions: false, calendar: false });

  const flush = useCallback(() => {
    timer.current = null;
    const w = want.current;
    want.current = { calls: false, decisions: false, calendar: false };
    if (w.calls) void qc.invalidateQueries({ queryKey: callsKeys.all(projectId) });
    else if (w.decisions) void qc.invalidateQueries({ queryKey: callsKeys.decisions(projectId) });
    // Разговор с клиентом сам закрывает задачу «Перезвонить» (CRM, taskAutoClose) — календарь тоже перечитываем.
    if (w.calendar) void qc.invalidateQueries({ queryKey: ['crm', 'cal', projectId] });
  }, [qc, projectId]);

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  useCrmEvents(CALL_EVENT_TYPES, ({ type }) => {
    const resync = type === RESYNC;
    if (resync || type === 'call.ringing' || type === 'call.finished' || type === 'call.changed') {
      want.current.calls = true;
    }
    if (type === 'call.decision' || type === 'call.changed') want.current.decisions = true;
    if (resync || type === 'call.finished' || type === 'call.changed' || type === 'booking.created') want.current.calendar = true;
    if (!timer.current && (want.current.calls || want.current.decisions || want.current.calendar)) {
      timer.current = setTimeout(flush, 800);
    }
  }, status === 'ready' && !!projectId);
}
